import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { inr } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Share2 } from "lucide-react";

export const THEMES = {
  sunset: { label: "Sunset", bg: ["#F97316", "#DB2777", "#7C3AED"], ink: "#FFFFFF", soft: "rgba(255,255,255,0.18)", accent: "#FDE68A" },
  midnight: { label: "Midnight", bg: ["#0F172A", "#1E3A8A", "#0B1220"], ink: "#F8FAFC", soft: "rgba(255,255,255,0.08)", accent: "#F5C518" },
};

const wrap = (ctx, text, maxW, maxLines) => {
  const words = (text || "").split(/\s+/); const lines = []; let cur = "";
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t;
    if (lines.length === maxLines) break;
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  if (lines.length === maxLines && words.join(" ") !== lines.join(" ")) lines[maxLines - 1] = lines[maxLines - 1].replace(/\s?\S*$/, "…");
  return lines;
};

const loadImg = (src) => new Promise((res) => { if (!src) return res(null); const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = () => res(null); im.src = src; });

export async function drawVoucherCard(canvas, voucher, theme, settings) {
  const S = 1080, ctx = canvas.getContext("2d"); canvas.width = S; canvas.height = S;
  const t = THEMES[theme] || THEMES.sunset;
  const g = ctx.createLinearGradient(0, 0, S, S); t.bg.forEach((c, i) => g.addColorStop(i / (t.bg.length - 1), c));
  ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
  for (let i = 0; i < 3; i++) { ctx.beginPath(); ctx.arc(S * (0.15 + i * 0.4), S * (i % 2 ? 0.92 : 0.08), 220 + i * 60, 0, Math.PI * 2); ctx.fillStyle = t.soft; ctx.fill(); }
  // ticket panel
  const px = 90, py = 150, pw = S - 180, ph = S - 300, r = 48;
  ctx.save(); ctx.shadowColor = "rgba(0,0,0,0.35)"; ctx.shadowBlur = 60; ctx.shadowOffsetY = 20;
  ctx.fillStyle = "rgba(255,255,255,0.12)"; ctx.beginPath(); ctx.roundRect(px, py, pw, ph, r); ctx.fill(); ctx.restore();
  ctx.strokeStyle = "rgba(255,255,255,0.45)"; ctx.lineWidth = 3; ctx.setLineDash([14, 12]); ctx.beginPath(); ctx.roundRect(px + 18, py + 18, pw - 36, ph - 36, r - 14); ctx.stroke(); ctx.setLineDash([]);
  // notches
  ctx.fillStyle = t.bg[1]; [py + ph * 0.62].forEach((y) => { ctx.beginPath(); ctx.arc(px, y, 28, 0, Math.PI * 2); ctx.arc(px + pw, y, 28, 0, Math.PI * 2); ctx.fill(); });
  ctx.fillStyle = t.ink; ctx.textAlign = "center";
  const logo = await loadImg(settings?.logo_url);
  if (logo) { const h = 70, w = h * (logo.width / logo.height); ctx.drawImage(logo, S / 2 - w / 2, py + 50, w, h); }
  else { ctx.font = "700 44px 'Playfair Display', Georgia, serif"; ctx.fillText(settings?.brand_name || "Safarshala", S / 2, py + 100); }
  ctx.font = "600 26px Manrope, Arial, sans-serif"; ctx.globalAlpha = 0.85; ctx.fillText("GIFT VOUCHER", S / 2, py + 165); ctx.globalAlpha = 1;
  ctx.font = "800 150px 'Playfair Display', Georgia, serif"; ctx.fillText(inr(voucher.amount), S / 2, py + 320);
  ctx.font = "500 30px Manrope, Arial, sans-serif"; ctx.fillText(`for ${voucher.recipient_name || "you"}`, S / 2, py + 375);
  if (voucher.message) { ctx.font = "italic 400 28px Georgia, serif"; ctx.globalAlpha = 0.9; wrap(ctx, `“${voucher.message}”`, pw - 160, 2).forEach((l, i) => ctx.fillText(l, S / 2, py + 435 + i * 38)); ctx.globalAlpha = 1; }
  // code pill
  const cy = py + ph * 0.62 + 40;
  ctx.fillStyle = "rgba(0,0,0,0.28)"; ctx.beginPath(); ctx.roundRect(S / 2 - 300, cy, 600, 96, 48); ctx.fill();
  ctx.fillStyle = t.accent; ctx.font = "700 52px 'Courier New', monospace"; ctx.fillText(voucher.code || "GIFT-XXXX-XXXX", S / 2, cy + 65);
  ctx.fillStyle = t.ink; ctx.font = "500 26px Manrope, Arial, sans-serif"; ctx.globalAlpha = 0.85;
  ctx.fillText(`Valid till ${voucher.valid_until || ""}`, S / 2, cy + 150);
  ctx.globalAlpha = 1; ctx.font = "600 30px Manrope, Arial, sans-serif";
  ctx.fillText(`Redeem at ${(settings?.site_url || "safarshala.in").replace(/^https?:\/\//, "").replace(/\/$/, "")}`, S / 2, S - 90);
  ctx.font = "400 22px Manrope, Arial, sans-serif"; ctx.globalAlpha = 0.7; ctx.fillText("Apply this code at checkout on any Safarshala trip", S / 2, S - 50); ctx.globalAlpha = 1;
}

export const VoucherCardDialog = ({ voucher, onClose }) => {
  const { settings } = useSettings();
  const [theme, setTheme] = useState("sunset");
  const ref = useRef(null);
  const [el, setEl] = useState(null);
  const setCanvas = (node) => { ref.current = node; setEl(node); };
  useEffect(() => { if (voucher && el) drawVoucherCard(el, voucher, theme, settings).catch((e) => console.error("voucher card", e)); }, [voucher, theme, settings, el]);
  const toBlob = () => new Promise((res) => ref.current.toBlob(res, "image/png"));
  const fname = `Safarshala_Gift_${voucher?.code}.png`;
  const download = async () => { const b = await toBlob(); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = fname; a.click(); URL.revokeObjectURL(a.href); };
  const share = async () => {
    const b = await toBlob(); const file = new File([b], fname, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) { try { await navigator.share({ files: [file], title: "Safarshala Gift Voucher", text: `Gift voucher ${voucher.code} · ${inr(voucher.amount)}` }); } catch { /* cancelled */ } }
    else { await download(); toast.info("Image downloaded — attach it in WhatsApp or Instagram."); }
  };
  return (
    <Dialog open={!!voucher} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="voucher-card-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Shareable voucher card</DialogTitle></DialogHeader>
        <div className="flex gap-2">{Object.entries(THEMES).map(([k, t]) => (
          <button key={k} type="button" onClick={() => setTheme(k)} className={`flex-1 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${theme === k ? "bg-foreground text-background" : "hover:bg-muted"}`} data-testid={`voucher-theme-${k}`}>
            <span className="inline-block h-3 w-3 rounded-full mr-2 align-middle" style={{ background: `linear-gradient(135deg, ${t.bg[0]}, ${t.bg[2]})` }} />{t.label}
          </button>))}
        </div>
        <canvas ref={setCanvas} className="w-full rounded-2xl shadow-lg aspect-square" data-testid="voucher-card-canvas" />
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="rounded-full" onClick={download} data-testid="voucher-card-download"><Download className="h-4 w-4 mr-1" /> Download PNG</Button>
          <Button className="rounded-full" onClick={share} data-testid="voucher-card-share"><Share2 className="h-4 w-4 mr-1" /> Share</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">1080×1080 — perfect for Instagram posts & WhatsApp status.</p>
      </DialogContent>
    </Dialog>
  );
};
